import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRef } from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api } from "@/lib/client"
import { queryKeys } from "@/lib/queryKeys"
import { cn } from "@/lib/utils"

type Appearance = "light" | "dark"

export function ProductImagesFields({
  imageUrlDark,
  imageUrlLight,
  onDarkChange,
  onLightChange,
  productId,
}: {
  imageUrlDark: string
  imageUrlLight: string
  onDarkChange: (url: string) => void
  onLightChange: (url: string) => void
  productId: number
}) {
  const queryClient = useQueryClient()
  const upload = useMutation({
    mutationFn: ({ appearance, file }: { appearance: Appearance; file: File }) =>
      api.uploadProductImage(productId, appearance, file),
    onError: (err) => toast.error(err.message),
    onSuccess: async (data, { appearance }) => {
      const url = appearance === "light" ? data.product.imageUrlLight : data.product.imageUrlDark
      if (appearance === "light") onLightChange(url ?? "")
      else onDarkChange(url ?? "")
      toast.success(`${appearance === "light" ? "Light" : "Dark"} image uploaded`)
      await queryClient.invalidateQueries({ queryKey: queryKeys.product(String(productId)) })
    },
  })

  return (
    <div className="grid gap-4 md:col-span-2 md:grid-cols-2">
      <ImageSlot
        appearance="light"
        busy={upload.isPending}
        onChange={onLightChange}
        onUpload={(file) => upload.mutate({ appearance: "light", file })}
        url={imageUrlLight}
      />
      <ImageSlot
        appearance="dark"
        busy={upload.isPending}
        onChange={onDarkChange}
        onUpload={(file) => upload.mutate({ appearance: "dark", file })}
        url={imageUrlDark}
      />
    </div>
  )
}

function ImageSlot({
  appearance,
  busy,
  onChange,
  onUpload,
  url,
}: {
  appearance: Appearance
  busy: boolean
  onChange: (url: string) => void
  onUpload: (file: File) => void
  url: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const isDark = appearance === "dark"

  return (
    <div className="space-y-2">
      <Label>{isDark ? "Dark image" : "Light image"}</Label>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border",
            isDark ? "bg-zinc-950" : "bg-zinc-100",
          )}
        >
          {url ? (
            <img alt={`${appearance} product`} className="size-full object-cover" src={url} />
          ) : (
            <span className={cn("text-[10px]", isDark ? "text-zinc-500" : "text-zinc-400")}>None</span>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <Input
            onChange={(e) => onChange(e.target.value)}
            placeholder={isDark ? "https://…/dark.webp" : "https://…/light.webp"}
            value={url}
          />
          <div className="flex flex-wrap gap-2">
            <input
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ""
                if (file) onUpload(file)
              }}
              ref={inputRef}
              type="file"
            />
            <Button
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              size="sm"
              type="button"
              variant="outline"
            >
              Upload
            </Button>
            <Button disabled={!url} onClick={() => onChange("")} size="sm" type="button" variant="ghost">
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
